import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'

import { t } from 'i18next'
import AvatarDropDown, { ThemeProps } from 'components/AvatarDropDown'
import Tooltip from 'share-ui/components/Tooltip/Tooltip'
import Chats from 'share-ui/components/Icon/Icons/components/Chats'
import {
    AddOutline,
    DollarOutline,
    Logs,
    Person,
    Search,
    Teams,
} from 'share-ui/components/Icon/Icons'
import ModeSwitcher from 'components/ModeSwitcher'
import { useAppModeContext } from 'context/AppModeContext'
import Key from 'share-ui/components/Icon/Icons/components/Key'
import { accountTypeEnum } from 'types/account'
import Subnet from 'share-ui/components/Icon/Icons/components/Subnet'
import { AuthContext } from 'contexts'

interface NavigationPermissions {
    chat: boolean
    subnet: boolean
    apiKey: boolean
    billing: boolean
    admin: boolean
    teams: boolean
    playground: boolean
    browse: boolean
    logs: boolean
}

const getNavigationList = ({
    permission,
    pathname,
}: {
    permission: NavigationPermissions
    pathname: string
}) => [
    {
        name: 'playground',
        icon: <StyledChatsIcon size={40} picked={pathname.includes('/playground')} />,
        route: '/playground',
        is_visible: permission.playground,
        rootRoute: '/playground',
    },
    {
        name: 'browse',
        icon: <StyledExploreIcon size={50} picked={pathname.includes('/browse')} />,
        route: '/browse',
        is_visible: permission.browse,
        rootRoute: '/browse',
    },
    {
        name: 'chat',
        icon: <StyledChatsIcon size={40} picked={pathname.includes('/chat')} />,
        route: '/chat',
        is_visible: permission.chat,
        rootRoute: '/chat',
    },
    {
        name: 'subnets',
        icon: <StyledSubnetIcon size={26} picked={pathname.includes('/subnets')} />,
        route: '/subnets',
        is_visible: permission.subnet,
        rootRoute: '/subnets',
    },
    {
        name: 'api-keys',
        icon: <StyledAPIIcon size={40} picked={pathname.includes('/api-key')} />,
        route: '/api-key',
        is_visible: permission.apiKey,
        rootRoute: '/api-key',
    },
    {
        name: 'logs',
        icon: <StyledLogsIcon size={34} picked={pathname.includes('/logs')} />,
        route: '/logs',
        is_visible: permission.logs,
        rootRoute: '/logs',
    },
    {
        name: 'billing',
        icon: <StyledBillingIcon picked={pathname.includes('/billing')} />,
        route: '/billing',
        is_visible: permission.billing,
        rootRoute: '/billing',
    },
    {
        name: 'teams',
        icon: <StyledTeamsIcon picked={pathname.includes('/teams')} />,
        route: '/teams',
        is_visible: permission.teams,
        rootRoute: '/teams',
    },
    {
        name: 'admin',
        icon: <StyledAdminIcon picked={pathname.includes('/admin')} size={28} />,
        route: '/admin',
        is_visible: permission.admin,
        rootRoute: '/admin',
    },
]

const MainNavigation = ({ restricted, theme }: { restricted?: boolean; theme: ThemeProps }) => {
    const { selected_account } = useAppModeContext()
    const { user } = useContext(AuthContext)

    const devMode = localStorage.getItem('dev_mode') === 'true' || false

    const navigate = useNavigate()

    const { pathname } = useLocation()

    const [active, setActive] = useState<string[]>([])

    const onHandleClick = (navigation_name: string) => {
        navigate(navigation_name)
    }

    useEffect(() => {
        const pathArr = pathname ? pathname.split('/') : []

        setActive(pathArr)
    }, [pathname])

    const isAdmin = user?.role === 'admin'

    const permission = {
        playground: false,
        chat: false,
        subnet: !isAdmin,
        apiKey: !isAdmin && ![accountTypeEnum.Validation].includes(selected_account?.account_type),
        billing: !isAdmin && ![accountTypeEnum.Validation].includes(selected_account?.account_type),
        teams: !isAdmin && false,
        admin: isAdmin,
        browse: false,
        logs: !isAdmin && ![accountTypeEnum.Validation].includes(selected_account?.account_type),
    }

    const navigation_list = getNavigationList({ permission, pathname })

    return (
        <StyledContainer>
            <StyledRoot>
                <StyledUl>
                    <Tooltip content={t('home')} position={Tooltip?.positions?.LEFT}>
                        <StyledLi
                            isActive={active[1] === ''}
                            onClick={() => {
                                if (!restricted) {
                                    onHandleClick('/')
                                }
                            }}
                        >
                            <StyledLogo src={'/DaturaLogo.svg'} />
                        </StyledLi>
                    </Tooltip>

                    {!restricted && (
                        <>
                            {navigation_list
                                .filter(navigation => navigation.is_visible)
                                .map((navigation, index: number) => (
                                    <Tooltip
                                        content={t(navigation.name)}
                                        position={Tooltip?.positions?.LEFT}
                                        key={index}
                                    >
                                        <StyledLi
                                            isActive={pathname.includes(navigation.rootRoute)}
                                            onClick={() => navigate(navigation.route)}
                                        >
                                            {navigation.icon}
                                            {pathname.includes(navigation.route) && (
                                                <StyledCorner />
                                            )}
                                        </StyledLi>
                                    </Tooltip>
                                ))}
                        </>
                    )}
                </StyledUl>
                <StyledBottomSection>
                    {devMode && (
                        <StyledInnerWrapper>
                            <ModeSwitcher />
                        </StyledInnerWrapper>
                    )}

                    <StyledInnerWrapper>
                        <AvatarDropDown theme={theme} devMode={devMode} />
                    </StyledInnerWrapper>
                </StyledBottomSection>
            </StyledRoot>
        </StyledContainer>
    )
}

export default MainNavigation

const StyledContainer = styled.div`
    height: 100vh;

    width: 80px;
    min-width: 80px;
    max-width: 80px;
`

const StyledRoot = styled.div`
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;

    padding-bottom: 24px;
`

const StyledUl = styled.ul`
    height: 100%;

    list-style: none;

    overflow-y: auto;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    padding: 24px 16px;
`
const StyledLi = styled.li<{ isActive?: boolean }>`
    color: transparent;
    position: relative;
    display: flex;
    width: 48px;
    height: 48px;
    justify-content: center;
    align-items: center;

    cursor: pointer;

    ${({ isActive }) =>
        isActive &&
        `
    opacity: 1;
    
    border-radius: 100px;
    background: #000;

    span{
      color: #FFF;
    }
    

    path {
    stroke: #FFF;
  
    }
`}
`

export const StyledAddIcon = styled(AddOutline)`
    path {
        fill: ${({ theme }) => theme.body.iconColor};
    }
`

const StyledBottomSection = styled.div`
    margin-top: auto;

    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 10px;
`
const StyledInnerWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`
export const StyledLogo = styled.img`
    width: 40px;
    height: 40px;
`

const StyledCorner = styled.div`
    width: 0;
    height: 0;
    border-right: 10px solid ${({ theme }) => theme.body.componentsWrapperBg};
    border-top: 16px solid transparent;
    border-bottom: 16px solid transparent;
    position: absolute;
    top: 8px;
    right: -16px;
`
const StyledAPIIcon = styled(Key)<{ picked: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
        stroke: transparent;
    }
`

const StyledBillingIcon = styled(DollarOutline)<{ picked: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
        stroke: transparent;
    }
`

const StyledSubnetIcon = styled(Subnet)<{ picked?: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
    }
`
const StyledAdminIcon = styled(Person)<{ picked?: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
        stroke: transparent;
    }
`
const StyledExploreIcon = styled(Search)<{ picked?: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
        stroke: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
    }
`
const StyledTeamsIcon = styled(Teams)<{ picked?: boolean }>`
    path {
        fill: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
        stroke: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
    }
`

const StyledChatsIcon = styled(Chats)<{ picked?: boolean }>`
    path {
        stroke: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
    }
`
const StyledLogsIcon = styled(Logs)<{ picked?: boolean }>`
    path {
        stroke: ${({ theme, picked }) => (picked ? '#FFF' : theme.body.iconColor)};
    }
`
